import { Component, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { ServiceBasicInfoStore } from './service-basic-info.store';
import { FilterInput } from '@it2go/data-grid';
import { FormBuilder, Validators } from '@angular/forms';
import {
    AddressDetailObject,
    ServiceBasicInfoCreateInput,
    ServiceBasicInfoGridItemObject,
    ServiceBasicInfoUpdateInput,
} from '@it2go/types';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { mapAddress } from '../../../../shared/helpers/map-address.helper';
import { isInvalid } from '../../../../shared/helpers/validate';
import { ServiceBasicInfoFormComponent } from '../service-basic-info-form/service-basic-info-form.component';
import { dateField } from '../../../../shared/components/kit/table/table.fields';
import { CardComponent } from '../../../../../components/kit/card/card.component';
import { AsyncPipe } from '@angular/common';
import { RuianService } from '../../../../shared/components/map/ruian.service';

@Component({
    selector: 'app-service-basic-info',
    templateUrl: './service-basic-info.component.html',
    styleUrl: './service-basic-info.component.scss',
    providers: [ServiceBasicInfoStore, RuianService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CardComponent,
        AsyncPipe,
        ServiceBasicInfoFormComponent,
        TableComponent,
    ],
})
export class ServiceBasicInfoComponent extends SubscriptionComponent {

    protected readonly store = inject(ServiceBasicInfoStore);
    private readonly fb = inject(FormBuilder);
    private readonly confirmBuilder = inject(ConfirmBuilder);
    private readonly ruianService = inject(RuianService);

    serviceId: number | null = null;

    basicInfosAdditionalFilter: FilterInput[][] = [];

    form = this.fb.group({
        id: this.fb.control<number | null>(null),
        centerId: this.fb.control<number | null>(null),
        validFrom: this.fb.control<Date | null>(null, Validators.required),
        name: this.fb.control<string | null>(null, Validators.required),
        regionId: this.fb.control<number | null>(null, Validators.required),
        territorialVzpWorkplaceCode: this.fb.control<string | null>(null, Validators.required),
        registeredFrom: this.fb.control<Date | null>(null, Validators.required),
        registeredTo: this.fb.control<Date | null>(null),
        ic: this.fb.control<string | null>(null, Validators.maxLength(8)),
        icz: this.fb.control<string | null>(null, Validators.maxLength(8)),
        form: this.fb.control<string[] | null>(null, Validators.required),
        address: this.fb.control<AddressDetailObject | null>(null, Validators.required),
        phone: this.fb.control<string | null>(null),
        email: this.fb.control<string | null>(null, Validators.email),
        userId: this.fb.control<string | null>(null, Validators.required),
        systemFrom: this.fb.control<Date | null>(null),
        systemTo: this.fb.control<Date | null>(null),
        bankAccountNumberPrefix: this.fb.control<string | null>(null, Validators.maxLength(6)),
        bankAccountNumber: this.fb.control<string | null>(null, [Validators.required, Validators.maxLength(10)]),
        bankCode: this.fb.control<string | null>(null, Validators.required),
        variableSymbol: this.fb.control<string | null>(null, Validators.maxLength(6)),
        image: this.fb.control<string | null>(null),

        // social
        identifier: this.fb.control<string | null>(null),
        regionReportingIds: this.fb.control<number[] | null>(null),
        sharePersons: this.fb.control<boolean | null>(null),
        regionScopeIds: this.fb.control<number[] | null>(null),
    });

    columns = [
        { name: 'name' },
        dateField('validFrom', false, false),
        dateField('validTo', false, false),
        { name: 'phone' },
        { name: 'email' },
    ];

    @ViewChild('serviceBasicInfosTable') serviceBasicInfosTable!: TableComponent;
    @ViewChild(ServiceBasicInfoFormComponent) serviceBasicInfoForm!: ServiceBasicInfoFormComponent;

    constructor() {
        super();

        this.store.doOnActions(
            () => this.serviceBasicInfosTable.reload(),
            'createServiceBasicInfo',
            'updateServiceBasicInfo',
            'deleteServiceBasicInfo',
        );

        this.store.registerSuccessMessage('createServiceBasicInfo', 'service.basicInfo.basicInfo.createServiceBasicInfoSuccess');
        this.store.registerSuccessMessage('updateServiceBasicInfo', 'service.basicInfo.basicInfo.updateServiceBasicInfoSuccess');

        this.subs.push(
            this.store.routeParam.serviceId.subscribe((serviceId) => {
                this.serviceId = Number(serviceId);
                if (this.serviceId) {
                    this.basicInfosAdditionalFilter = [[{ column: 'serviceId', operator: 'EQ', value: [serviceId] }]];
                }
            }),
            this.store.data.serviceBasicInfos$.subscribe((serviceBasicInfos) => {
                if (serviceBasicInfos?.['items'].length) {
                    let currentBasicInfo: ServiceBasicInfoGridItemObject | null = null;
                    if (this.form.controls.id.value) {
                        // some service basic info was selected before
                        currentBasicInfo = serviceBasicInfos['items'].find((it: ServiceBasicInfoGridItemObject) => {
                            return it.id === this.form.controls.id.value;
                        });
                    }
                    if (!currentBasicInfo) {
                        // currently valid service basic info
                        currentBasicInfo = serviceBasicInfos['items'].find((it: ServiceBasicInfoGridItemObject) => {
                            return new Date(it.validFrom) <= new Date() && (!it.validTo || new Date(it.validTo) >= new Date());
                        });
                    }
                    if (currentBasicInfo) {
                        this.detail(currentBasicInfo);
                    }
                }
            }),
        );
    }

    detail(basicInfo: ServiceBasicInfoGridItemObject) {
        this.form.patchValue({
            ...basicInfo,
            territorialVzpWorkplaceCode: basicInfo.territorialVzpWorkplace?.code || null,
            validFrom: new Date(basicInfo.validFrom),
            registeredFrom: basicInfo.registeredFrom ? new Date(basicInfo.registeredFrom) : null,
            registeredTo: basicInfo.registeredTo ? new Date(basicInfo.registeredTo) : null,
            systemFrom: basicInfo.systemFrom ? new Date(basicInfo.systemFrom) : null,
            systemTo: basicInfo.systemTo ? new Date(basicInfo.systemTo) : null,
        });
    }

    protected delete(event: any): void {
        this.confirmBuilder.confirm(
            'service.basicInfo.basicInfo.deleteServiceBasicInfoConfirmation.header',
            'service.basicInfo.basicInfo.deleteServiceBasicInfoConfirmation.label',
            () => {
                this.store.api.deleteServiceBasicInfo({ id: event.item.id });
            },
            { name: 'serviceBasicInfoDeleteConfirm' + event.item.id },
            'pi-trash',
            event?.event?.target,
        );
    }

    async submit(create: boolean): Promise<void> {
        if (isInvalid(this.form)) return;

        const address = await this.appendOrp(mapAddress(this.form.controls.address.value!));
        let data: any = {
            centerId: this.form.controls.centerId.value,
            validFrom: this.form.controls.validFrom.value!.toISOString(),
            name: this.form.controls.name.value!,
            regionId: this.form.controls.regionId.value!,
            registeredFrom: this.form.controls.registeredFrom.value!.toISOString(),
            registeredTo: this.form.controls.registeredTo.value?.toISOString() || null,
            ic: this.form.controls.ic.value || null,
            form: this.form.controls.form.value!,
            address,
            phone: this.form.controls.phone.value || null,
            email: this.form.controls.email.value || null,
            userId: this.form.controls.userId.value!,
            systemFrom: this.form.controls.systemFrom.value?.toISOString() || null,
            systemTo: this.form.controls.systemTo.value?.toISOString() || null,
            bankAccountNumberPrefix: this.form.controls.bankAccountNumberPrefix.value || null,
            bankAccountNumber: this.form.controls.bankAccountNumber.value!,
            bankCode: this.form.controls.bankCode.value!,
            variableSymbol: this.form.controls.variableSymbol.value || null,
        };

        if (this.serviceBasicInfoForm.isSocial()) {
            data = {
                ...data,
                identifier: this.form.controls.identifier.value || null,
                regionReportingIds: this.form.controls.regionReportingIds.value || null,
                sharePersons: this.form.controls.sharePersons.value || null,
                regionScopeIds: this.form.controls.regionScopeIds.value || null,
            };
        }

        if (this.serviceBasicInfoForm.isHealthCare()) {
            data = {
                ...data,
                territorialVzpWorkplaceCode: this.form.controls.territorialVzpWorkplaceCode.value!,
                icz: this.form.controls.icz.value || null,
            };
        }

        if (create && this.serviceId) {
            this.store.api.createServiceBasicInfo({
                serviceId: this.serviceId,
                ...data,
            } as ServiceBasicInfoCreateInput);
        } else if (!create && this.form.controls.id.value) {
            this.store.api.updateServiceBasicInfo({
                id: this.form.controls.id.value!,
                ...data,
            } as ServiceBasicInfoUpdateInput);
        }

        this.form.markAsUntouched();
    }

    private async appendOrp(address: AddressDetailObject): Promise<AddressDetailObject> {
        const orp = await this.ruianService.getOrp(address.position);
        if (!orp) return address;

        return {
            ...address,
            regionalStructure: [
                ...address.regionalStructure.filter((item) => item.type !== 'regional.orp'),
                { name: orp.nazev, type: 'regional.orp', isoCode: null },
            ],
        };
    }
}
